"use server";

import { cookies } from "next/headers";
import { redirect } from "next/navigation";
import { AUTH_COOKIE_NAME, signToken } from "@/lib/core/auth";

/** Maximum cookie age in seconds — matches JWT `exp` (24 h). */
const COOKIE_MAX_AGE_SECONDS = 86_400;

/** Shape returned by {@link handleLogin} to the client form. */
export interface LoginActionState {
  error?: string;
}

/**
 * Server Action — authenticates the user against env credentials and sets the JWT cookie.
 *
 * @param _prev - Previous action state (required by `useActionState` contract).
 * @param formData - Must contain `username` and `password` fields.
 * @returns An object with an optional `error` message when authentication fails.
 *          On success the function redirects to `/` (never returns to the caller).
 */
export async function handleLogin(
  _prev: LoginActionState,
  formData: FormData
): Promise<LoginActionState> {
  try {
    const username = formData.get("username");
    const password = formData.get("password");

    if (typeof username !== "string" || typeof password !== "string") {
      return { error: "Preencha todos os campos." };
    }

    const isValid =
      username === process.env.ADMIN_USERNAME &&
      password === process.env.ADMIN_PASSWORD;

    if (!isValid) {
      return { error: "Credenciais inválidas." };
    }

    const token = await signToken({ sub: username });

    const cookieStore = await cookies();
    cookieStore.set(AUTH_COOKIE_NAME, token, {
      httpOnly: true,
      secure: process.env.NODE_ENV === "production",
      sameSite: "lax",
      maxAge: COOKIE_MAX_AGE_SECONDS,
      path: "/",
    });
  } catch {
    return { error: "Erro interno. Tente novamente." };
  }

  redirect("/");
}

/**
 * Server Action — clears the auth cookie and redirects to the login page.
 */
export async function handleLogout(): Promise<void> {
  const cookieStore = await cookies();
  cookieStore.delete(AUTH_COOKIE_NAME);
  redirect("/login");
}
